import { useId, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type AffiliateConfig, type Campaign, type CampaignMode, type PlatformConfig, type CampaignPacing, type CampaignStatus, type ChannelRules, type ScheduleItem, type Target } from '@/api/client'
import { qk } from '@/lib/queries'
import { dayKey, parseTime } from '@/lib/time'
import { cn } from '@/lib/utils'
import { platformName } from '@/ui'
import { byAttention } from '../schedule/PostLine'

export const MODES: { value: CampaignMode; label: string; note: string }[] = [
  { value: 'manual', label: '수동', note: '직접 쓴 글만. 생성기가 건드리지 않는다' },
  { value: 'topic', label: '주제 풀', note: '정해 둔 주제로 생성. 트렌드와 무관' },
  { value: 'trend', label: '트렌드', note: '트렌드에서 주제를 자동으로 고른다' },
  { value: 'brand', label: '브랜드', note: '브랜드와 트렌드 훅을 섞는다' },
  { value: 'repurpose', label: '재가공', note: '내 원본 글을 다른 채널용으로 바꾼다' },
  { value: 'affiliate', label: '쿠팡', note: '쿠팡 시그널로 제휴 글을 쓴다. Threads 계정 하나에 올린다' },
]

export const modeLabel = (m: CampaignMode) => MODES.find((x) => x.value === m)?.label ?? m

/** The modes a select offers. 재가공 has no generator behind it yet — choosing
 *  it creates a campaign that never writes anything — so it is left out, and
 *  stays in the list only for a campaign that already has it. 쿠팡 has its own
 *  settings (categories, tone), which only the create dialog and the campaign's
 *  쿠팡 section edit, so a campaign cannot be switched to it from the pacing
 *  form: it is offered when creating, or for a campaign that already is one. */
export function selectableModes(current?: CampaignMode, creating = false) {
  return MODES.filter((m) => {
    if (m.value === 'affiliate') return creating || current === 'affiliate'
    if (m.value === 'repurpose') return current === 'repurpose'
    return true
  })
}

/** Accounts that can post to a campaign platform. A "reddit" campaign posts
 *  through either kind of Reddit account. */
export function targetsFor(platform: string, targets: Target[], keep?: number): Target[] {
  const kinds = platform === 'reddit' ? ['reddit', 'reddit_browser'] : [platform]
  return targets.filter((t) => t.id === keep || (kinds.includes(t.platform) && t.status === 'active'))
}

/** Platforms whose posts go to a named channel (a subreddit). The channel gap
 *  only means something there; Threads and the rest have no channel to space. */
const CHANNEL_PLATFORMS = ['reddit', 'reddit_browser']
export const hasChannels = (platform: string) => CHANNEL_PLATFORMS.includes(platform)

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/

/** Below this the day's posts no longer fit the window (pacing.MinSpacing). */
const MIN_SPACING_MIN = 30
/** Another campaign's or the desk's post on the same account is kept at least
 *  this far away, or the spacing when that is shorter (pacing.DefaultAccountFloor). */
export const ACCOUNT_FLOOR_MIN = 60

function windowMinutes(start: string, end: string): number {
  const a = HHMM.exec(start)
  const b = HHMM.exec(end)
  if (a && b) {
    const w = Number(b[1]) * 60 + Number(b[2]) - (Number(a[1]) * 60 + Number(a[2]))
    if (w > 0) return w
  }
  return 24 * 60 - 1
}

/** How far apart a campaign's own posts sit on one day, in minutes: the
 *  window shared out so the day's posts all fit from its opening, with room
 *  for the 15-minute search step and up to 20 minutes of jitter per slot.
 *  Same arithmetic as the server's pacing.Spacing and pacing.Fits. A missing
 *  or bad window is the whole day; one a day has nothing to space. */
export function spacing(postsPerDay: number, start: string, end: string): { minutes: number; fits: boolean } {
  const w = windowMinutes(start, end)
  if (!(postsPerDay > 1)) return { minutes: w, fits: true }
  const raw = Math.floor(((w - 20) / (postsPerDay - 1) - 35) / 5) * 5
  return { minutes: Math.max(MIN_SPACING_MIN, raw), fits: raw >= MIN_SPACING_MIN }
}

/** "2시간", "3시간 45분", "30분". */
export function durationText(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}분`
  return m === 0 ? `${h}시간` : `${h}시간 ${m}분`
}

export const isHHMM = (v: string) => HHMM.test(v)

export function enabledPlatforms(c: Pick<Campaign, 'PlatformConfigs'>): string[] {
  return Object.entries(c.PlatformConfigs ?? {})
    .filter(([, v]) => v.enabled)
    .map(([k]) => k)
}

/** One gap, said for every platform that sets it: "6시간" when they all agree,
 *  "Threads 6시간 · Reddit 3시간" when they do not. */
function gapText(entries: [string, PlatformConfig][], key: 'channel_cooldown_hours'): string | null {
  const set = entries.filter(([, v]) => Number(v[key]) > 0)
  if (set.length === 0) return null
  const first = Number(set[0][1][key])
  const same = set.every(([, v]) => Number(v[key]) === first)
  if (same && set.length === entries.length) return `${first}시간`
  return set.map(([k, v]) => `${platformName(k)} ${Number(v[key])}시간`).join(' · ')
}

/** A campaign's cadence in one line — what decides when an approved draft
 *  goes out. The spacing follows from the posts per day and the window; the
 *  channel gap is read from every enabled platform that has channels. */
export function cadence(c: Campaign): string {
  const all = Object.entries(c.PlatformConfigs ?? {})
  const on = all.filter(([, v]) => v.enabled)
  const entries = on.length > 0 ? on : all
  const cool = gapText(entries.filter(([k]) => hasChannels(k)), 'channel_cooldown_hours')
  const bits = [`하루 ${c.PostsPerDay}건`]
  if (c.PostingWindowStart && c.PostingWindowEnd) bits.push(`${c.PostingWindowStart}–${c.PostingWindowEnd}`)
  if (c.PostsPerDay > 1) bits.push(`간격 ${durationText(spacing(c.PostsPerDay, c.PostingWindowStart ?? '', c.PostingWindowEnd ?? '').minutes)}`)
  if (cool) bits.push(`채널 간격 ${cool}`)
  return bits.join(' · ')
}

/** Banned phrases are written one rule per line as "subreddit: phrase", which
 *  is how an operator thinks about them, and parsed back into channel_rules. */
export function rulesToText(rules: Record<string, ChannelRules> | undefined): string {
  if (!rules) return ''
  const lines: string[] = []
  for (const [channel, r] of Object.entries(rules)) for (const phrase of r.banned_phrases ?? []) lines.push(`${channel}: ${phrase}`)
  return lines.join('\n')
}

export function textToRules(
  text: string,
  existing: Record<string, ChannelRules> | undefined,
): Record<string, ChannelRules> {
  const out: Record<string, ChannelRules> = {}
  // Cooldowns already set per channel are kept; only phrases come from the text.
  for (const [channel, r] of Object.entries(existing ?? {})) if (r.cooldown_hours) out[channel] = { cooldown_hours: r.cooldown_hours }
  for (const line of text.split('\n')) {
    const i = line.indexOf(':')
    if (i < 1) continue
    const channel = line.slice(0, i).trim()
    const phrase = line.slice(i + 1).trim()
    if (!channel || !phrase) continue
    const entry = out[channel] ?? {}
    entry.banned_phrases = [...(entry.banned_phrases ?? []), phrase]
    out[channel] = entry
  }
  return out
}

/** Writes that change a campaign; each reads the list and the campaign again. */
/** The resume toast's second line, when passed slots were taken back. */
export function unslottedLine(n?: number) {
  return n ? ` 멈춘 동안 시각이 지난 글 ${n}건은 슬롯 없음으로 옮겼다.` : ''
}

export function useCampaignMutations(id?: number) {
  const qc = useQueryClient()
  const done = () => {
    void qc.invalidateQueries({ queryKey: qk.campaigns })
    if (id) void qc.invalidateQueries({ queryKey: qk.campaign(id) })
  }
  return {
    setStatus: useMutation({
      mutationFn: (v: { id: number; status: CampaignStatus }) => api.setCampaignStatus(v.id, v.status),
      onSettled: done,
    }),
    pacing: useMutation({
      mutationFn: (v: { id: number; pacing: CampaignPacing }) => api.updateCampaignPacing(v.id, v.pacing),
      onSettled: done,
    }),
    /** Replaces the whole Coupang settings object (the server takes it whole). */
    affiliate: useMutation({
      mutationFn: (v: { id: number; affiliate: AffiliateConfig }) => api.updateCampaignAffiliate(v.id, v.affiliate),
      onSettled: done,
    }),
    remove: useMutation({ mutationFn: (cid: number) => api.deleteCampaign(cid), onSettled: done }),
    create: useMutation({ mutationFn: (c: Partial<Campaign>) => api.createCampaign(c), onSettled: done }),
  }
}

/** A titled card on the campaign page. The heading names the region, so a
 *  screen reader lands on "페이싱" or "발행한 글" by name. */
export function Section({
  title,
  aside,
  actions,
  label,
  children,
  className,
  id: anchor,
}: {
  /** An in-page anchor (`#id`) for a link to jump to. */
  id?: string
  title: ReactNode
  /** Quiet text after the title: a count, a period. */
  aside?: ReactNode
  /** Buttons and links on the right of the heading. */
  actions?: ReactNode
  /** The region's name when the heading carries more than the name. */
  label?: string
  children: ReactNode
  className?: string
}) {
  const id = useId()
  return (
    <section
      id={anchor}
      aria-labelledby={label ? undefined : id}
      aria-label={label}
      className={cn('min-w-0 space-y-3 rounded-lg border border-border bg-card p-4', className)}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h2 id={id} className="min-w-0 text-section font-semibold">
          {title}
          {aside != null && aside !== false && <span className="ml-2 text-table font-normal text-muted-foreground">{aside}</span>}
        </h2>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  )
}

/** "2026-10-22" for an instant on a campaign's clock — the day its posts per
 *  day are counted in. An unknown zone falls back to the browser's. */
export function zoneDayKey(d: Date, tz?: string): string {
  if (!tz) return dayKey(d)
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
  } catch {
    return dayKey(d)
  }
}

/** Today against the day's limit, counted the way the server fills it
 *  (pacing.dayCount): posts that went up today plus slots booked for today,
 *  on the campaign's clock. */
export function todayOf(items: ScheduleItem[], c: Pick<Campaign, 'Timezone' | 'PostsPerDay'>, now = new Date()) {
  const key = zoneDayKey(now, c.Timezone)
  const today = (iso: string | null) => {
    const d = parseTime(iso)
    return !!d && zoneDayKey(d, c.Timezone) === key
  }
  let published = 0
  let booked = 0
  for (const it of items) {
    if (it.status === 'published') {
      if (today(it.published_at)) published++
    } else if (it.status === 'scheduled' || it.status === 'publishing') {
      if (today(it.scheduled_at)) booked++
    }
  }
  return { published, booked, limit: Math.max(1, c.PostsPerDay || 1) }
}

/** What waits to go out — being sent, booked, approved with no slot yet — in
 *  the order the campaign cards list it. */
export function upcomingOf(items: ScheduleItem[]): ScheduleItem[] {
  return items.filter((it) => it.status === 'publishing' || it.status === 'scheduled' || it.status === 'approved').sort(byAttention)
}

/** The booked slots still ahead, soonest first. */
export function nextSlots(items: ScheduleItem[], now = new Date()): ScheduleItem[] {
  return items
    .filter((it) => it.status === 'scheduled' && (parseTime(it.scheduled_at)?.getTime() ?? 0) > now.getTime())
    .sort((a, b) => (parseTime(a.scheduled_at)?.getTime() ?? 0) - (parseTime(b.scheduled_at)?.getTime() ?? 0))
}
